@if (loading()) {
  <!-- Loading skeleton — mirrors full-width layout -->
  <div class="flex w-full min-w-0 flex-col gap-4 animate-pulse" aria-busy="true" aria-label="Loading TikTok connection status">
    <div class="overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800">
      <div class="h-44 bg-gray-200 dark:bg-zinc-800 lg:h-48"></div>
      <div class="grid grid-cols-1 sm:grid-cols-3">
        <div class="h-24 border-t border-zinc-200 bg-gray-100 dark:border-zinc-800 dark:bg-zinc-900 sm:border-t-0 sm:border-l sm:first:border-l-0"></div>
        <div class="h-24 border-t border-zinc-200 bg-gray-100 dark:border-zinc-800 dark:bg-zinc-900 sm:border-t-0 sm:border-l"></div>
        <div class="h-24 border-t border-zinc-200 bg-gray-100 dark:border-zinc-800 dark:bg-zinc-900 sm:border-t-0 sm:border-l"></div>
      </div>
    </div>
    <div class="h-28 rounded-2xl bg-gray-200 dark:bg-zinc-800"></div>
  </div>
} @else if (connection()) {
  <!-- Connected state -->
  <div class="flex w-full min-w-0 flex-col gap-4">

    <!-- Status header card -->
    <div class="primary-card-container flex items-start gap-4 p-5 sm:p-6">
      <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black dark:bg-zinc-800">
        <svg class="h-6 w-6 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07Z"/>
        </svg>
      </div>
      <div class="min-w-0 flex-1">
        <div class="mb-1 flex flex-wrap items-center gap-2">
          <h2 class="m-0 text-base font-semibold text-primary">TikTok for Business</h2>
          <span class="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <span class="h-1.5 w-1.5 rounded-full bg-green-500" aria-hidden="true"></span>
            Connected
          </span>
        </div>
        <p class="m-0 text-sm text-secondary">Your TikTok Ads account is linked. You can boost creator posts from your briefs.</p>
      </div>
    </div>

    <!-- Account detail cards -->
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
      <div class="primary-card-container p-5">
        <p class="mb-1.5 text-xs font-medium uppercase tracking-wider text-secondary">Advertiser account(s)</p>
        @if (connection()!.advertiserAccountIds && connection()!.advertiserAccountIds!.length > 1) {
          <ul class="space-y-1">
            @for (id of connection()!.advertiserAccountIds!; track id) {
              <li class="truncate font-mono text-sm font-medium text-primary">{{ id }}</li>
            }
          </ul>
        } @else {
          <p class="m-0 truncate font-mono text-sm font-medium text-primary">{{ connection()!.advertiserAccountId ?? '—' }}</p>
        }
      </div>
      <div class="primary-card-container p-5">
        <p class="mb-1.5 text-xs font-medium uppercase tracking-wider text-secondary">Account status</p>
        <div class="flex items-center gap-2">
          <span class="h-2 w-2 shrink-0 rounded-full" [class]="connection()!.isActive ? 'bg-green-500' : 'bg-zinc-400'" aria-hidden="true"></span>
          <p class="m-0 text-sm font-medium text-primary">{{ connection()!.isActive ? 'Active' : 'Inactive' }}</p>
        </div>
      </div>
    </div>

    <!-- Active features -->
    <div class="primary-card-container p-5 sm:p-6">
      <h3 class="mb-4 text-xs font-semibold uppercase tracking-wider text-secondary">What you can do now</h3>
      <ul class="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        <li class="flex gap-3">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30" aria-hidden="true">
            <svg class="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </div>
          <div class="min-w-0">
            <p class="m-0 text-sm font-medium text-primary">Spark Ads</p>
            <p class="mt-0.5 text-sm leading-relaxed text-secondary">Boost creator posts from your briefs as paid ads.</p>
          </div>
        </li>
        <li class="flex gap-3">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30" aria-hidden="true">
            <svg class="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </div>
          <div class="min-w-0">
            <p class="m-0 text-sm font-medium text-primary">Targeted reach</p>
            <p class="mt-0.5 text-sm leading-relaxed text-secondary">Run ads across TikTok with interest and demographic targeting.</p>
          </div>
        </li>
        <li class="flex gap-3">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30" aria-hidden="true">
            <svg class="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
            </svg>
          </div>
          <div class="min-w-0">
            <p class="m-0 text-sm font-medium text-primary">Analytics</p>
            <p class="mt-0.5 text-sm leading-relaxed text-secondary">Track impressions, clicks, and conversions in real time.</p>
          </div>
        </li>
      </ul>
    </div>

    @if (error()) {
      <div class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/20" role="alert">
        <p class="m-0 text-sm text-red-600 dark:text-red-400">{{ error() }}</p>
      </div>
    }

    <div class="flex flex-col gap-3 border-t border-zinc-200 pt-5 dark:border-zinc-800 sm:flex-row sm:items-center sm:justify-between">
      <p class="m-0 text-sm text-secondary">You can disconnect anytime. Spark Ads in progress may need to finish in TikTok Ads Manager.</p>
      <button
        type="button"
        (click)="disconnect()"
        [disabled]="disconnecting()"
        class="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg border border-red-200 px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
      >
        @if (disconnecting()) { Disconnecting… } @else { Disconnect account }
      </button>
    </div>

  </div>
} @else {
  <!-- Disconnected — connect CTA -->
  <div class="flex w-full min-w-0 flex-col gap-4">

    <section class="overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950" aria-labelledby="tiktok-connect-heading">
      <!-- Hero -->
      <div class="relative flex flex-col gap-6 overflow-hidden bg-zinc-950 px-6 py-7 sm:px-8 sm:py-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10 lg:px-10 lg:py-9">
        <div class="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-linear-to-l from-white/5 to-transparent" aria-hidden="true"></div>

        <div class="relative min-w-0 flex-1">
          <div class="mb-4 flex flex-wrap items-center gap-2">
            <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10" aria-hidden="true">
              <svg class="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07Z"/>
              </svg>
            </div>
            <p class="m-0 text-sm font-medium text-white/70">TikTok for Business</p>
            <p class="m-0 rounded-md bg-white/10 px-2 py-0.5 text-xs font-medium text-white/60">Official connection</p>
          </div>

          <h2 id="tiktok-connect-heading" class="m-0 text-2xl font-bold leading-tight text-pretty text-white lg:text-3xl">
            Turn creator posts into paid performance
          </h2>
          <p class="mt-2 max-w-xl text-sm leading-relaxed text-white/65 lg:text-base">
            One connection lets you boost approved Shout submissions as TikTok Spark Ads — without rebuilding creative elsewhere.
          </p>
        </div>

        <div class="relative flex w-full min-w-0 flex-col gap-2 sm:max-w-sm lg:w-auto lg:shrink-0">
          <button
            type="button"
            (click)="connect()"
            [disabled]="connecting()"
            aria-label="Connect your TikTok for Business account using secure sign-in"
            class="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-black transition-colors hover:bg-zinc-100 active:bg-zinc-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            @if (connecting()) {
              <svg class="h-4 w-4 animate-spin text-black" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <span>Opening TikTok…</span>
            } @else {
              <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07Z"/>
              </svg>
              <span>Connect TikTok for Business</span>
            }
          </button>
          <p class="m-0 text-center text-xs leading-relaxed text-white/45 sm:text-left">
            Secure sign-in with TikTok. We never see or store your password.
          </p>
        </div>
      </div>

      <!-- Benefit strip — one surface with the hero -->
      <ul class="grid grid-cols-1 divide-y divide-zinc-200 dark:divide-zinc-800 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <li class="flex gap-3 px-5 py-5 sm:px-6">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900" aria-hidden="true">
            <svg class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <h3 class="m-0 text-sm font-semibold text-primary">Spark Ads</h3>
            <p class="mt-1 text-sm leading-relaxed text-secondary">Promote real creator posts from your briefs — no rebuild needed.</p>
          </div>
        </li>
        <li class="flex gap-3 px-5 py-5 sm:px-6">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900" aria-hidden="true">
            <svg class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <h3 class="m-0 text-sm font-semibold text-primary">Targeted reach</h3>
            <p class="mt-1 text-sm leading-relaxed text-secondary">Use TikTok targeting to reach the right audiences for your brand.</p>
          </div>
        </li>
        <li class="flex gap-3 px-5 py-5 sm:px-6">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900" aria-hidden="true">
            <svg class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <h3 class="m-0 text-sm font-semibold text-primary">Campaign analytics</h3>
            <p class="mt-1 text-sm leading-relaxed text-secondary">See impressions, engagement, and conversions for boosted posts.</p>
          </div>
        </li>
      </ul>
    </section>

    @if (error()) {
      <div class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/20" role="alert">
        <p class="m-0 text-sm text-red-600 dark:text-red-400">{{ error() }}</p>
      </div>
    }

    <!-- How it works -->
    <section class="primary-card-container p-5 sm:p-6" aria-labelledby="tiktok-how-heading">
      <h3 id="tiktok-how-heading" class="mb-5 text-xs font-semibold uppercase tracking-wider text-secondary">How it works</h3>
      <ol class="grid grid-cols-1 gap-5 sm:gap-6 lg:grid-cols-3 lg:gap-0">
        <li class="flex gap-3 lg:pr-8">
          <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-950 dark:bg-white" aria-hidden="true">
            <p class="m-0 text-xs font-bold leading-none text-white dark:text-black">1</p>
          </div>
          <div class="min-w-0 pt-0.5">
            <h4 class="m-0 text-sm font-medium text-primary">Connect your account</h4>
            <p class="mt-1 text-sm leading-relaxed text-secondary">Sign in with TikTok's secure flow — usually under a minute.</p>
          </div>
        </li>
        <li class="flex gap-3 border-t border-zinc-200 pt-5 dark:border-zinc-700 lg:border-l lg:border-t-0 lg:px-8 lg:pt-0">
          <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-950 dark:bg-white" aria-hidden="true">
            <p class="m-0 text-xs font-bold leading-none text-white dark:text-black">2</p>
          </div>
          <div class="min-w-0 pt-0.5">
            <h4 class="m-0 text-sm font-medium text-primary">Choose a creator post</h4>
            <p class="mt-1 text-sm leading-relaxed text-secondary">Pick an approved brief submission you want to amplify.</p>
          </div>
        </li>
        <li class="flex gap-3 border-t border-zinc-200 pt-5 dark:border-zinc-700 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
          <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-950 dark:bg-white" aria-hidden="true">
            <p class="m-0 text-xs font-bold leading-none text-white dark:text-black">3</p>
          </div>
          <div class="min-w-0 pt-0.5">
            <h4 class="m-0 text-sm font-medium text-primary">Launch and measure</h4>
            <p class="mt-1 text-sm leading-relaxed text-secondary">Set budget and audience, then track performance in real time.</p>
          </div>
        </li>
      </ol>
    </section>

  </div>
}
